import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  ApproveInsuranceClaimPayload,
  CloseInsuranceClaimPayload,
  CreateInsuranceClaimPaymentPayload,
  CreateInsuranceClaimPayload,
  CreateInsuranceClaimRecoveryPayload,
  CreateInsuranceClaimReservePayload,
  CreateInsuranceHealthClaimLinePayload,
  CreateInsurancePreAuthorizationPayload,
  DeclineInsuranceClaimPayload,
  InsuranceEligibilityCheckResult,
  InsuranceApiService,
  InsuranceClaimDetail,
  InsuranceProviderListItem,
  InsurancePolicyOption,
  UpdateInsuranceClaimPayload
} from './insurance-api.service';
import { InsuranceAiCopilotPanelComponent } from './insurance-ai-copilot-panel.component';
import { InsuranceFraudAlertBannerComponent } from './insurance-fraud-alert-banner.component';
import { INSURANCE_CLAIM_INVESTIGATION_FIELDS } from './insurance-claim-investigation-schemas';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { serializeStructuredJsonPayload, parseStructuredJsonPayload } from '../../shared/design-system/structured-json-form.utils';

type ClaimTab = 'fnol' | 'reserves' | 'investigation' | 'payments' | 'healthLines' | 'recovery' | 'documents' | 'audit';

@Component({
  standalone: true,
  selector: 'ain-insurance-claim-detail',
  imports: [
    AinAmountPipe,
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CurrencySelectorComponent,
    JalaliDateInputComponent,
    InfoHintComponent,
    PartySelectorComponent,
    ButtonModule,
    DialogModule,
    TableModule,
    InsuranceAiCopilotPanelComponent,
    InsuranceFraudAlertBannerComponent,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.claimDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.claims.help.detail.title'"
        [descriptionKey]="'insurance.claims.help.detail.description'"
        [bulletKeys]="[
          'insurance.claims.help.detail.bullets.lifecycle',
          'insurance.claims.help.detail.bullets.tabs',
          'insurance.claims.help.detail.bullets.fnol'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/claims']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !canSaveFnol()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.claims.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.claims.help.info.title'"
              [descriptionKey]="'insurance.claims.help.info.description'"
              [bulletKeys]="[
                'insurance.claims.help.info.bullets.policy',
                'insurance.claims.help.info.bullets.claimant',
                'insurance.claims.help.info.bullets.dates'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label>
              <span>{{ t('insurance.claims.fields.policyNumber') }}</span>
              <p-select
                [options]="policySelectOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="createForm.policyId"
                (ngModelChange)="onCreatePolicyChange($event)"
                name="policyId"
                [showClear]="true"
                [filter]="true"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.claimNumber') }}</span>
              <input pInputText [(ngModel)]="createForm.claimNumber" name="claimNumber" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.claimType') }}</span>
              <p-select
                [options]="claimTypeOptions"
                optionValue="value"
                [(ngModel)]="createForm.claimType"
                name="claimType"
                [filter]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.fnolChannel') }}</span>
              <p-select [options]="fnolChannelOptions" optionValue="value" [(ngModel)]="createForm.fnolChannel" name="fnolChannel" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.reserve') }}</span>
              <p-inputNumber [(ngModel)]="createForm.reserveAmount" name="reserveAmount" [min]="0" />
            </label>
            <label>
              <span>{{ t('insurance.policies.fields.currencyCode') }}</span>
              <ain-currency-selector [(ngModel)]="createForm.currencyCode" name="currencyCode" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.openedAt') }}</span>
              <ain-date-input [(ngModel)]="createForm.reportedAtUtc" name="reportedAtUtc" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.lossDate') }}</span>
              <ain-date-input [(ngModel)]="createForm.lossDateUtc" name="lossDateUtc" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.claims.fields.description') }}</span>
              <input pInputText [(ngModel)]="createForm.description" name="description" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.partyId') }}</span>
              <ain-party-selector [(ngModel)]="createClaimantPartyId" name="createClaimantPartyId" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.documentType') }}</span>
              <p-select
                [options]="documentTypeOptions"
                optionValue="value"
                [(ngModel)]="createDocumentType"
                name="createDocumentType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.documentRef') }}</span>
              <input pInputText [(ngModel)]="createDocumentRef" name="createDocumentRef" />
            </label>
          </form>
        </section>
        @if (error()) { <p class="error">{{ error() }}</p> }
      } @else if (!claim()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/claims']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving() || !canSaveFnol()" (click)="saveEdit()"></button>
        </div>

        <ain-insurance-fraud-alert-banner [claimId]="claim()!.id" (changed)="onFraudAlertChanged()" />

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.claims.sections.info') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.claims.help.info.title'"
              [descriptionKey]="'insurance.claims.help.info.description'"
              [bulletKeys]="[
                'insurance.claims.help.info.bullets.policy',
                'insurance.claims.help.info.bullets.claimant',
                'insurance.claims.help.info.bullets.dates'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.claims.fields.claimNumber') }}: <strong>{{ claim()!.claimNumber }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ t(statusKey(claim()!.status)) }}</strong></span>
            <span>{{ t('insurance.claims.fields.policyNumber') }}: <strong>{{ claim()!.policyNumber }}</strong></span>
            <span>{{ t('insurance.claims.fields.reserve') }}: <strong>{{ claim()!.reserveAmount | ainAmount }} {{ claim()!.currencyCode }}</strong></span>
            <span>{{ t('insurance.claims.fields.paid') }}: <strong>{{ claim()!.paidAmount | ainAmount }} {{ claim()!.currencyCode }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.claims.sections.lifecycle') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.claims.help.lifecycle.title'"
              [descriptionKey]="'insurance.claims.help.lifecycle.description'"
              [bulletKeys]="[
                'insurance.claims.help.lifecycle.bullets.reason',
                'insurance.claims.help.lifecycle.bullets.transitions'
              ]" />
          </div>
          <div class="lifecycle-actions">
            <input pInputText [(ngModel)]="actionReason" [placeholder]="t('insurance.claimDetail.fields.actionReason')" />
            <button pButton type="button" [label]="t('insurance.claimDetail.actions.approve')" [disabled]="saving()" (click)="approveClaim()"></button>
            <button pButton type="button" severity="danger" [label]="t('insurance.claimDetail.actions.decline')" [disabled]="saving()" (click)="declineClaim()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.claimDetail.actions.close')" [disabled]="saving()" (click)="closeClaim()"></button>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'fnol'" (click)="activeTab.set('fnol')">{{ t('insurance.claimDetail.tabs.fnol') }}</button>
            <button type="button" [class.active]="activeTab() === 'reserves'" (click)="activeTab.set('reserves')">{{ t('insurance.claimDetail.tabs.reserves') }}</button>
            <button type="button" [class.active]="activeTab() === 'investigation'" (click)="activeTab.set('investigation')">{{ t('insurance.claimDetail.tabs.investigation') }}</button>
            <button type="button" [class.active]="activeTab() === 'payments'" (click)="activeTab.set('payments')">{{ t('insurance.claimDetail.tabs.payments') }}</button>
            <button type="button" [class.active]="activeTab() === 'healthLines'" (click)="activeTab.set('healthLines')">{{ t('insurance.claimDetail.tabs.healthLines') }}</button>
            <button type="button" [class.active]="activeTab() === 'recovery'" (click)="activeTab.set('recovery')">{{ t('insurance.claimDetail.tabs.recovery') }}</button>
            <button type="button" [class.active]="activeTab() === 'documents'" (click)="activeTab.set('documents')">{{ t('insurance.claimDetail.tabs.documents') }}</button>
            <button type="button" [class.active]="activeTab() === 'audit'" (click)="activeTab.set('audit')">{{ t('insurance.claimDetail.tabs.audit') }}</button>
          </div>

          @if (activeTab() === 'fnol') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.fnol') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.claims.help.tabs.fnol.title'"
                [descriptionKey]="'insurance.claims.help.tabs.fnol.description'"
                [bulletKeys]="[
                  'insurance.claims.help.tabs.fnol.bullets.channel',
                  'insurance.claims.help.tabs.fnol.bullets.save'
                ]" />
            </div>
            <div class="summary-grid">
              <p><strong>{{ t('insurance.claims.fields.openedAt') }}:</strong> {{ claim()!.reportedAtUtc | ainDate }}</p>
              <p><strong>{{ t('insurance.claims.fields.lossDate') }}:</strong> {{ claim()!.lossDateUtc | ainDate }}</p>
              <p><strong>{{ t('insurance.claims.fields.policyNumber') }}:</strong> {{ claim()!.policyNumber }}</p>
              <p><strong>{{ t('insurance.claims.fields.fnolChannel') }}:</strong> {{ t(channelKey(claim()!.fnolChannel)) }}</p>
            </div>
            <form class="grid section-gap">
              <label class="wide">
                <span>{{ t('insurance.claims.fields.description') }}</span>
                <input pInputText [(ngModel)]="editForm.description" name="editDescription" />
              </label>
              <label>
                <span>{{ t('insurance.claims.fields.claimType') }}</span>
                <p-select
                  [options]="claimTypeOptions"
                  optionValue="value"
                  [(ngModel)]="editForm.claimType"
                  name="editClaimType"
                  [filter]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.claims.fields.fnolChannel') }}</span>
                <p-select [options]="fnolChannelOptions" optionValue="value" [(ngModel)]="editForm.fnolChannel" name="editFnolChannel" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </form>
          } @else if (activeTab() === 'reserves') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.reserves') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.claims.help.tabs.reserves.title'"
                  [descriptionKey]="'insurance.claims.help.tabs.reserves.description'"
                  [bulletKeys]="[
                    'insurance.claims.help.tabs.reserves.bullets.movement',
                    'insurance.claims.help.tabs.reserves.bullets.types'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openReserveCreate()"></button>
              </div>
            </div>
            @if (claim()!.reserves.length === 0) {
              <p>{{ t('insurance.claimDetail.states.noReserves') }}</p>
            } @else {
              <p-table [value]="claim()!.reserves" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.claims.fields.reserveType') }}</th>
                    <th>{{ t('insurance.claims.fields.reserveMovementType') }}</th>
                    <th>{{ t('insurance.claims.fields.reserve') }}</th>
                    <th>{{ t('columns.date') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ enumLabel(reserveTypeOptions, row.reserveType) }}</td>
                    <td>{{ enumLabel(movementTypeOptions, row.movementType) }}</td>
                    <td>{{ row.amount | ainAmount }} {{ claim()!.currencyCode }}</td>
                    <td>{{ row.createdAtUtc | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'investigation') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.investigation') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.claims.help.tabs.investigation.title'"
                [descriptionKey]="'insurance.claims.help.tabs.investigation.description'"
                [bulletKeys]="['insurance.claims.help.tabs.investigation.bullets.notes']" />
            </div>
            <p>{{ t('insurance.claimDetail.investigationHint') }}</p>
            <p><strong>{{ t('insurance.claims.fields.description') }}:</strong> {{ claim()!.description }}</p>
            <div class="wide section-gap">
              <span class="field-label">{{ t('insurance.claimDetail.investigation.fields.notes') }}</span>
              <ain-structured-json-form
                [fields]="investigationFields"
                [(payloadJson)]="investigationNotesJson" />
            </div>
          } @else if (activeTab() === 'payments') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.payments') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.claims.help.tabs.payments.title'"
                  [descriptionKey]="'insurance.claims.help.tabs.payments.description'"
                  [bulletKeys]="[
                    'insurance.claims.help.tabs.payments.bullets.payee',
                    'insurance.claims.help.tabs.payments.bullets.confirm'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openPaymentCreate()"></button>
              </div>
            </div>
            @if (claim()!.payments.length === 0) {
              <p>{{ t('insurance.claimDetail.states.noPayments') }}</p>
            } @else {
              <p-table [value]="claim()!.payments" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('insurance.claims.fields.paid') }}</th>
                    <th>{{ t('columns.date') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ enumLabel(paymentStatusOptions, row.status) }}</td>
                    <td>{{ row.amount | ainAmount }} {{ claim()!.currencyCode }}</td>
                    <td>{{ row.createdAtUtc | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'healthLines') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.healthLines') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.claims.help.tabs.healthLines.title'"
                  [descriptionKey]="'insurance.claims.help.tabs.healthLines.description'"
                  [bulletKeys]="[
                    'insurance.claims.help.tabs.healthLines.bullets.provider',
                    'insurance.claims.help.tabs.healthLines.bullets.eligibility'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openHealthLineCreate()"></button>
              </div>
            </div>
            @if (claim()!.healthLines.length === 0) {
              <p>{{ t('insurance.health.claimLines.empty') }}</p>
            } @else {
              <p-table [value]="claim()!.healthLines" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.health.claimLines.fields.serviceCode') }}</th>
                    <th>{{ t('insurance.health.claimLines.fields.billedAmount') }}</th>
                    <th>{{ t('insurance.health.claimLines.fields.serviceDate') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ enumLabel(healthServiceCodeOptions, row.serviceCode) }}</td>
                    <td>{{ row.billedAmount | ainAmount }} / {{ row.allowedAmount | ainAmount }}</td>
                    <td>{{ row.serviceDate | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'recovery') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.recovery') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.claims.help.tabs.recovery.title'"
                  [descriptionKey]="'insurance.claims.help.tabs.recovery.description'"
                  [bulletKeys]="[
                    'insurance.claims.help.tabs.recovery.bullets.subrogation',
                    'insurance.claims.help.tabs.recovery.bullets.reference'
                  ]" />
                <button pButton type="button" [label]="t('common.add')" (click)="openRecoveryCreate()"></button>
              </div>
            </div>
            @if (claim()!.recoveries.length === 0) {
              <p>{{ t('insurance.claimDetail.states.noRecoveries') }}</p>
            } @else {
              <p-table [value]="claim()!.recoveries" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.claims.fields.recoveryType') }}</th>
                    <th>{{ t('insurance.claims.fields.recoveryAmount') }}</th>
                    <th>{{ t('columns.date') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ enumLabel(recoveryTypeOptions, row.recoveryType) }}</td>
                    <td>{{ row.amount | ainAmount }} {{ claim()!.currencyCode }}</td>
                    <td>{{ row.createdAtUtc | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else if (activeTab() === 'documents') {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.documents') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.claims.help.tabs.documents.title'"
                [descriptionKey]="'insurance.claims.help.tabs.documents.description'"
                [bulletKeys]="['insurance.claims.help.tabs.documents.bullets.attachments']" />
            </div>
            @if (claim()!.documents.length === 0) {
              <p>{{ t('insurance.claimDetail.states.noDocuments') }}</p>
            } @else {
              <p-table [value]="claim()!.documents" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.claims.fields.documentType') }}</th>
                    <th>{{ t('columns.title') }}</th>
                    <th>{{ t('insurance.claims.fields.documentRef') }}</th>
                    <th>{{ t('columns.date') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ enumLabel(documentTypeOptions, row.documentType) }}</td>
                    <td>{{ row.title }}</td>
                    <td>{{ row.sourceRef }}</td>
                    <td>{{ row.createdAtUtc | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.claimDetail.tabs.audit') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.claims.help.tabs.audit.title'"
                [descriptionKey]="'insurance.claims.help.tabs.audit.description'"
                [bulletKeys]="['insurance.claims.help.tabs.audit.bullets.trace']" />
            </div>
            <p>{{ t('insurance.claimDetail.auditStub') }}</p>
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }
        <ain-insurance-ai-copilot-panel entityType="InsuranceClaim" [entityId]="claim()!.id" />

        <p-dialog [header]="t('insurance.claimDetail.actions.addReserve')" [modal]="true" [(visible)]="reserveDialogVisible" [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.claims.fields.reserveType') }}</span>
              <p-select
                [options]="reserveTypeOptions"
                optionValue="value"
                [(ngModel)]="reserveForm.reserveType"
                name="dialogReserveType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.reserveMovementType') }}</span>
              <p-select
                [options]="movementTypeOptions"
                optionValue="value"
                [(ngModel)]="reserveForm.movementType"
                name="dialogMovementType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.reserve') }}</span>
              <p-inputNumber [(ngModel)]="reserveForm.amount" name="dialogReserveAmount" [min]="0.01" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="reserveDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveReserveDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.claimDetail.actions.addPayment')" [modal]="true" [(visible)]="paymentDialogVisible" [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label class="wide">
              <span>{{ t('insurance.claims.fields.partyId') }}</span>
              <ain-party-selector [(ngModel)]="paymentForm.payeePartyId" name="dialogPayeePartyId" />
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.paid') }}</span>
              <p-inputNumber [(ngModel)]="paymentForm.amount" name="dialogPaymentAmount" [min]="0.01" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="paymentDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="savePaymentDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.claimDetail.actions.addRecovery')" [modal]="true" [(visible)]="recoveryDialogVisible" [style]="{ width: '560px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.claims.fields.recoveryType') }}</span>
              <p-select
                [options]="recoveryTypeOptions"
                optionValue="value"
                [(ngModel)]="recoveryForm.recoveryType"
                name="dialogRecoveryType"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.claims.fields.recoveryAmount') }}</span>
              <p-inputNumber [(ngModel)]="recoveryForm.amount" name="dialogRecoveryAmount" [min]="0.01" />
            </label>
            <label class="wide">
              <span>{{ t('insurance.claims.fields.reference') }}</span>
              <input pInputText [(ngModel)]="recoveryForm.reference" name="dialogRecoveryReference" />
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="recoveryDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveRecoveryDialog()"></button>
          </ng-template>
        </p-dialog>

        <p-dialog [header]="t('insurance.health.claimLines.actions.add')" [modal]="true" [(visible)]="healthLineDialogVisible" [style]="{ width: '720px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.health.claimLines.fields.providerId') }}</span>
              <p-select [options]="providerOptions()" optionLabel="label" optionValue="value" [(ngModel)]="healthLineForm.providerId" name="dialogHealthProviderId" [showClear]="true" [filter]="true" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.health.claimLines.fields.serviceCode') }}</span>
              <p-select
                [options]="healthServiceCodeOptions"
                optionValue="value"
                [(ngModel)]="healthLineForm.serviceCode"
                name="dialogHealthServiceCode"
                [filter]="true"
                appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            <label>
              <span>{{ t('insurance.health.claimLines.fields.serviceDate') }}</span>
              <ain-date-input [(ngModel)]="healthLineForm.serviceDate" name="dialogHealthServiceDate" />
            </label>
            <label>
              <span>{{ t('insurance.health.claimLines.fields.billedAmount') }}</span>
              <p-inputNumber [(ngModel)]="healthLineForm.billedAmount" name="dialogHealthBilledAmount" [min]="0.01" />
            </label>
            <label>
              <span>{{ t('insurance.health.claimLines.fields.copay') }}</span>
              <p-inputNumber [(ngModel)]="healthLineForm.copay" name="dialogHealthCopay" [min]="0" />
            </label>
          </div>
          @if (eligibilityCheck()) {
            <p class="field-hint">
              {{ eligibilityCheck()!.isEligible ? t('insurance.health.claimLines.eligibility.eligible') : t('insurance.health.claimLines.eligibility.notEligible') }}
              @if (!eligibilityCheck()!.isEligible && eligibilityCheck()!.reasonCode) {
                <span>({{ eligibilityCheck()!.reasonCode }})</span>
              }
            </p>
          }
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('insurance.health.claimLines.actions.checkEligibility')" [disabled]="saving()" (click)="checkEligibility()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.health.claimLines.actions.createPreAuth')" [disabled]="saving()" (click)="createPreAuthorization()"></button>
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="healthLineDialogVisible.set(false)"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveHealthLineDialog()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .lifecycle-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .section-gap { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    .field-label { font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px; }
    .field-hint { font-size: 12px; color: #64748b; margin-top: 8px; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) {
      .grid, .summary-grid, .dialog-grid { grid-template-columns: 1fr; }
    }
  `]
})
export class InsuranceClaimDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly claim = signal<InsuranceClaimDetail | null>(null);
  readonly activeTab = signal<ClaimTab>('fnol');
  readonly isCreateMode = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly policyOptions = signal<InsurancePolicyOption[]>([]);
  readonly providers = signal<InsuranceProviderListItem[]>([]);
  readonly eligibilityCheck = signal<InsuranceEligibilityCheckResult | null>(null);
  readonly reserveDialogVisible = signal(false);
  readonly paymentDialogVisible = signal(false);
  readonly recoveryDialogVisible = signal(false);
  readonly healthLineDialogVisible = signal(false);
  createForm: CreateInsuranceClaimPayload = {
    policyId: '',
    legalEntityId: '',
    claimNumber: '',
    claimType: 'Accident',
    description: '',
    fnolChannel: 'Portal',
    lossDateUtc: new Date().toISOString(),
    reportedAtUtc: new Date().toISOString(),
    reserveAmount: 0,
    currencyCode: 'IRR',
    parties: [],
    documents: []
  };
  editForm: UpdateInsuranceClaimPayload = {
    claimType: 'Accident',
    description: '',
    fnolChannel: 'Portal',
    lossDateUtc: new Date().toISOString(),
    reportedAtUtc: new Date().toISOString(),
    currencyCode: 'IRR',
    notes: null
  };
  readonly fnolChannelOptions = [
    { value: 'Portal', labelKey: 'insurance.claims.channels.portal' },
    { value: 'Agent', labelKey: 'insurance.claims.channels.agent' },
    { value: 'CallCenter', labelKey: 'insurance.claims.channels.callCenter' },
    { value: 'Api', labelKey: 'insurance.claims.channels.api' }
  ];
  readonly claimTypeOptions = [
    { value: 'Accident', labelKey: 'insurance.claims.claimType.accident' },
    { value: 'PropertyDamage', labelKey: 'insurance.claims.claimType.propertyDamage' },
    { value: 'Health', labelKey: 'insurance.claims.claimType.health' },
    { value: 'Theft', labelKey: 'insurance.claims.claimType.theft' },
    { value: 'Fire', labelKey: 'insurance.claims.claimType.fire' },
    { value: 'Liability', labelKey: 'insurance.claims.claimType.liability' }
  ];
  readonly reserveTypeOptions = [
    { value: 'Indemnity', labelKey: 'insurance.claims.reserveType.indemnity' },
    { value: 'Expense', labelKey: 'insurance.claims.reserveType.expense' },
    { value: 'ALAE', labelKey: 'insurance.claims.reserveType.alae' }
  ];
  readonly movementTypeOptions = [
    { value: 'Increase', labelKey: 'insurance.claims.movementType.increase' },
    { value: 'Decrease', labelKey: 'insurance.claims.movementType.decrease' },
    { value: 'Set', labelKey: 'insurance.claims.movementType.set' },
    { value: 'Release', labelKey: 'insurance.claims.movementType.release' }
  ];
  readonly recoveryTypeOptions = [
    { value: 'Subrogation', labelKey: 'insurance.claims.recoveryType.subrogation' },
    { value: 'Salvage', labelKey: 'insurance.claims.recoveryType.salvage' },
    { value: 'Other', labelKey: 'insurance.claims.recoveryType.other' }
  ];
  readonly paymentStatusOptions = [
    { value: 'PendingApproval', labelKey: 'insurance.claims.paymentStatus.pendingApproval' },
    { value: 'Paid', labelKey: 'insurance.claims.paymentStatus.paid' }
  ];
  readonly documentTypeOptions = [
    { value: 'Photo', labelKey: 'insurance.claims.documentType.photo' },
    { value: 'Report', labelKey: 'insurance.claims.documentType.report' },
    { value: 'PoliceReport', labelKey: 'insurance.claims.documentType.policeReport' },
    { value: 'MedicalRecord', labelKey: 'insurance.claims.documentType.medicalRecord' },
    { value: 'Invoice', labelKey: 'insurance.claims.documentType.invoice' },
    { value: 'Estimate', labelKey: 'insurance.claims.documentType.estimate' },
    { value: 'Other', labelKey: 'insurance.claims.documentType.other' }
  ];
  readonly healthServiceCodeOptions = [
    { value: 'LAB01', labelKey: 'insurance.health.serviceCodes.lab01' },
    { value: 'MRI01', labelKey: 'insurance.health.serviceCodes.mri01' },
    { value: 'CONS01', labelKey: 'insurance.health.serviceCodes.cons01' },
    { value: 'RX01', labelKey: 'insurance.health.serviceCodes.rx01' },
    { value: 'ER01', labelKey: 'insurance.health.serviceCodes.er01' }
  ];
  readonly investigationFields = INSURANCE_CLAIM_INVESTIGATION_FIELDS;
  investigationNotesJson = '';
  createClaimantPartyId = '';
  createDocumentType = 'Photo';
  createDocumentRef = '';
  reserveForm: CreateInsuranceClaimReservePayload = { reserveType: 'Indemnity', amount: 0, movementType: 'Increase' };
  paymentForm: CreateInsuranceClaimPaymentPayload = { payeePartyId: '', amount: 0, approvalWorkflowId: null, paymentIntentId: null };
  healthLineForm: CreateInsuranceHealthClaimLinePayload = {
    serviceDate: new Date().toISOString(),
    providerId: '',
    serviceCode: '',
    billedAmount: 0,
    copay: 0,
    preAuthorizationId: null
  };
  recoveryForm: CreateInsuranceClaimRecoveryPayload = { amount: 0, recoveryType: 'Subrogation', reference: null };
  actionReason = '';

  ngOnInit(): void {
    this.api.listPolicyOptions().subscribe({ next: items => this.policyOptions.set(items) });
    this.api.listHealthProviders().subscribe({ next: items => this.providers.set(items), error: () => this.providers.set([]) });

    const claimId = this.route.snapshot.paramMap.get('claimId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';

    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!claimId) {
      this.loading.set(false);
      return;
    }

    this.api.getClaim(claimId).subscribe({
      next: item => {
        this.claim.set(item);
        if (item) {
          this.editForm = {
            claimType: item.claimType,
            description: item.description,
            fnolChannel: item.fnolChannel,
            lossDateUtc: item.lossDateUtc,
            reportedAtUtc: item.reportedAtUtc,
            currencyCode: item.currencyCode,
            notes: null
          };
        }
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.claim.set(null);
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.claims.form.createTitle')
      : this.t('insurance.claims.form.editTitle');
  }

  canSaveFnol(): boolean {
    if (this.isCreateMode()) {
      return !!(
        this.createForm.policyId &&
        this.createForm.claimNumber.trim() &&
        this.createClaimantPartyId.trim()
      );
    }

    return !!this.editForm.claimType.trim();
  }

  statusKey(status: InsuranceClaimDetail['status']): string {
    return `insurance.claims.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  channelKey(channel: string): string {
    return `insurance.claims.channels.${channel.charAt(0).toLowerCase()}${channel.slice(1)}`;
  }

  enumLabel(options: Array<{ value: string; labelKey: string }>, value: string): string {
    const option = options.find(item => item.value === value);
    return option ? this.t(option.labelKey) : value;
  }

  backToList(): void {
    void this.router.navigate(['/insurance/claims']);
  }

  saveCreate(): void {
    this.error.set('');
    if (!this.createClaimantPartyId.trim()) {
      this.error.set(this.t('insurance.claimDetail.messages.partyRequired'));
      return;
    }
    this.createForm.parties = [{ partyId: this.createClaimantPartyId.trim(), role: 'Claimant' }];
    this.createForm.documents = this.createDocumentRef.trim()
      ? [{
          documentType: this.createDocumentType.trim() || 'Photo',
          title: this.enumLabel(this.documentTypeOptions, this.createDocumentType) || 'Photo',
          sourceRef: this.createDocumentRef.trim()
        }]
      : [];
    this.saving.set(true);

    this.api.createClaim(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/claims', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.claim();
    if (!current) return;

    this.error.set('');
    const serializedNotes = serializeStructuredJsonPayload(parseStructuredJsonPayload(this.investigationNotesJson));
    this.editForm.notes = serializedNotes === '{}' ? null : serializedNotes;
    this.saving.set(true);
    this.api.updateClaim(current.id, this.editForm).subscribe({
      next: item => {
        this.claim.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  policySelectOptions(): Array<{ label: string; value: string }> {
    return this.policyOptions().map(item => ({
      value: item.id,
      label: item.policyNumber
    }));
  }

  onCreatePolicyChange(policyId: string | null): void {
    if (!policyId) {
      this.createForm.legalEntityId = '';
      this.createForm.currencyCode = 'IRR';
      return;
    }

    const selected = this.policyOptions().find(item => item.id === policyId);
    if (!selected) {
      return;
    }

    this.createForm.legalEntityId = selected.legalEntityId;
    this.createForm.currencyCode = selected.currencyCode;
  }

  openReserveCreate(): void {
    this.reserveForm = { reserveType: 'Indemnity', amount: 0, movementType: 'Increase' };
    this.reserveDialogVisible.set(true);
  }

  saveReserveDialog(): void {
    const current = this.claim();
    if (!current) return;
    this.saving.set(true);
    this.error.set('');
    this.api.addClaimReserve(current.id, this.reserveForm).subscribe({
      next: () => {
        this.reserveDialogVisible.set(false);
        this.reloadClaim(current.id);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openPaymentCreate(): void {
    // paymentIntentId omitted from UI — optional external finance reference; backend assigns when orchestrated.
    this.paymentForm = { payeePartyId: '', amount: 0, approvalWorkflowId: null, paymentIntentId: null };
    this.paymentDialogVisible.set(true);
  }

  savePaymentDialog(): void {
    const current = this.claim();
    if (!current) return;
    if (!confirm(this.t('insurance.claimDetail.messages.paymentConfirm'))) return;
    this.saving.set(true);
    this.error.set('');
    this.api.createClaimPayment(current.id, this.paymentForm).subscribe({
      next: () => {
        this.paymentDialogVisible.set(false);
        this.reloadClaim(current.id);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openRecoveryCreate(): void {
    this.recoveryForm = { amount: 0, recoveryType: 'Subrogation', reference: null };
    this.recoveryDialogVisible.set(true);
  }

  saveRecoveryDialog(): void {
    const current = this.claim();
    if (!current) return;
    this.saving.set(true);
    this.error.set('');
    this.api.createClaimRecovery(current.id, this.recoveryForm).subscribe({
      next: () => {
        this.recoveryDialogVisible.set(false);
        this.reloadClaim(current.id);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openHealthLineCreate(): void {
    this.eligibilityCheck.set(null);
    this.healthLineForm = {
      serviceDate: new Date().toISOString(),
      providerId: '',
      serviceCode: this.healthServiceCodeOptions[0].value,
      billedAmount: 0,
      copay: 0,
      preAuthorizationId: null
    };
    this.healthLineDialogVisible.set(true);
  }

  saveHealthLineDialog(): void {
    const current = this.claim();
    if (!current || !this.healthLineForm.providerId || !this.healthLineForm.serviceCode.trim()) return;
    this.saving.set(true);
    this.error.set('');
    this.api.addHealthClaimLine(current.id, this.healthLineForm).subscribe({
      next: () => {
        this.healthLineDialogVisible.set(false);
        this.reloadClaim(current.id);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  providerOptions(): Array<{ label: string; value: string }> {
    return this.providers().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  checkEligibility(): void {
    const current = this.claim();
    if (!current || !current.parties.length || !this.healthLineForm.providerId || !this.healthLineForm.serviceCode.trim()) {
      return;
    }
    this.error.set('');
    this.api.checkHealthEligibility({
      policyId: current.policyId,
      memberPartyId: current.parties[0].partyId,
      serviceDateUtc: this.healthLineForm.serviceDate,
      providerId: this.healthLineForm.providerId,
      serviceCode: this.healthLineForm.serviceCode
    }).subscribe({
      next: result => this.eligibilityCheck.set(result),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  createPreAuthorization(): void {
    const current = this.claim();
    if (!current || !this.healthLineForm.providerId || !this.healthLineForm.serviceCode.trim()) {
      return;
    }
    const payload: CreateInsurancePreAuthorizationPayload = {
      policyId: current.policyId,
      providerId: this.healthLineForm.providerId,
      serviceCode: this.healthLineForm.serviceCode.trim(),
      requestedAmount: this.healthLineForm.billedAmount,
      requestedAtUtc: null,
      validUntilUtc: null,
      status: 'Approved',
      approvedAmount: this.healthLineForm.billedAmount
    };
    this.saving.set(true);
    this.api.createHealthPreAuthorization(payload).subscribe({
      next: preAuth => {
        this.healthLineForm.preAuthorizationId = preAuth.id;
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  approveClaim(): void {
    const current = this.claim();
    const reason = this.actionReason.trim();
    if (!reason) {
      this.error.set(this.t('insurance.claimDetail.messages.reasonRequired'));
      return;
    }
    if (!current || !confirm(this.t('insurance.claimDetail.messages.approveConfirm'))) return;
    this.saving.set(true);
    const payload: ApproveInsuranceClaimPayload = { reason, approvalWorkflowId: null };
    this.api.approveClaim(current.id, payload).subscribe({
      next: item => {
        this.claim.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  declineClaim(): void {
    const current = this.claim();
    const reason = this.actionReason.trim();
    if (!reason) {
      this.error.set(this.t('insurance.claimDetail.messages.reasonRequired'));
      return;
    }
    if (!current || !confirm(this.t('insurance.claimDetail.messages.declineConfirm'))) return;
    this.saving.set(true);
    const payload: DeclineInsuranceClaimPayload = { reason };
    this.api.declineClaim(current.id, payload).subscribe({
      next: item => {
        this.claim.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  closeClaim(): void {
    const current = this.claim();
    const reason = this.actionReason.trim();
    if (!reason) {
      this.error.set(this.t('insurance.claimDetail.messages.reasonRequired'));
      return;
    }
    if (!current || !confirm(this.t('insurance.claimDetail.messages.closeConfirm'))) return;
    this.saving.set(true);
    const payload: CloseInsuranceClaimPayload = { reason };
    this.api.closeClaim(current.id, payload).subscribe({
      next: item => {
        this.claim.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  onFraudAlertChanged(): void {
    const id = this.claim()?.id;
    if (id) this.reloadClaim(id);
  }

  private reloadClaim(id: string): void {
    this.api.getClaim(id).subscribe({
      next: item => {
        this.claim.set(item);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
